import type { MarkerKind } from '@concept/protocol';
import type { EdgeIndicatorPosition } from './edge-indicators';

export type EdgeIndicatorCanvasPiece = {
  image: CanvasImageSource;
  kind: MarkerKind;
};

export type EdgeIndicatorCanvasItem = {
  count: number;
  pieces: EdgeIndicatorCanvasPiece[];
  position: EdgeIndicatorPosition;
};

type ViewportSize = { width: number; height: number };

const INDICATOR_SIZE = 48;
const INDICATOR_RADIUS = INDICATOR_SIZE / 2;

export function sizeEdgeIndicatorCanvas(
  canvas: HTMLCanvasElement,
  viewport: ViewportSize,
  devicePixelRatio: number,
) {
  const scale = Math.max(1, devicePixelRatio);
  const width = Math.max(1, Math.round(viewport.width * scale));
  const height = Math.max(1, Math.round(viewport.height * scale));
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const cssWidth = `${viewport.width}px`;
  const cssHeight = `${viewport.height}px`;
  if (canvas.style.width !== cssWidth) canvas.style.width = cssWidth;
  if (canvas.style.height !== cssHeight) canvas.style.height = cssHeight;
  return scale;
}

function drawPiece(
  context: CanvasRenderingContext2D,
  piece: EdgeIndicatorCanvasPiece,
  index: number,
) {
  if (index === 0) {
    const size = piece.kind === 'cube' ? 27 : 28;
    const height = piece.kind === 'cube' ? 27 : 30;
    context.drawImage(piece.image, -size / 2, -height / 2, size, height);
    return;
  }

  const x = index === 1 ? -17.2 : -4.8;
  const y = index === 1 ? -17.3 : -6.7;
  context.drawImage(piece.image, x, y, 22, 24);
}

export function drawEdgeIndicatorCanvas(
  context: CanvasRenderingContext2D,
  viewport: ViewportSize,
  pixelScale: number,
  items: EdgeIndicatorCanvasItem[],
) {
  context.setTransform(pixelScale, 0, 0, pixelScale, 0, 0);
  context.clearRect(0, 0, viewport.width, viewport.height);

  for (const { count, pieces, position } of items) {
    context.save();
    context.translate(position.x, position.y);

    context.save();
    context.rotate(position.angle * Math.PI / 180);
    context.beginPath();
    context.moveTo(34, 0);
    context.lineTo(23, -7);
    context.lineTo(23, 7);
    context.closePath();
    context.fillStyle = '#fffdf7';
    context.fill();
    context.restore();

    context.beginPath();
    context.arc(0, 0, INDICATOR_RADIUS - .5, 0, Math.PI * 2);
    context.fillStyle = '#fffdf7';
    context.fill();
    context.lineWidth = 1;
    context.strokeStyle = 'rgba(39, 67, 80, .22)';
    context.stroke();

    pieces.slice(0, 3).forEach((piece, index) => drawPiece(context, piece, index));

    if (count > 1) {
      const label = String(count);
      context.font = '700 10px ui-rounded, system-ui, sans-serif';
      const badgeWidth = Math.max(18, Math.ceil(context.measureText(label).width) + 8);
      const badgeX = 24 - badgeWidth / 2 + 3;
      const badgeY = 24 - 9 + 2;
      context.beginPath();
      context.roundRect(badgeX - badgeWidth / 2, badgeY - 9, badgeWidth, 18, 9);
      context.fillStyle = '#fffdf7';
      context.fill();
      context.beginPath();
      context.roundRect(badgeX - badgeWidth / 2 + 2, badgeY - 7, badgeWidth - 4, 14, 7);
      context.fillStyle = '#08749e';
      context.fill();
      context.fillStyle = '#fff';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(label, badgeX, badgeY + .5);
    }

    context.restore();
  }
}
